import { ElementType } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import checked from './static/img/checked.svg';
import disabledChecked from './static/img/checked-disabled.svg';
import { Colors } from '../../common/Colors';

export const checkFocus = css`
  border-color: rgba(49, 135, 238, 0.5);
  box-shadow: 0 0 0 0.133em rgba(69, 155, 255, 0.5);
`;

export const CheckFieldEmotion = styled('div')`
  font-size: 1.5rem;
  cursor: default;
  display: block;

  & + & {
    margin-top: 1.2rem;
  }
`;

export interface CheckboxProps {
  inputType: 'radio' | 'checkbox';
}

export const Content = styled('div')<CheckboxProps>`
  position: relative;
  display: flex;
  align-items: flex-start;
  line-height: 1.33;
  transition: color 0.5s ease-in;

  &::before {
    content: '';
    flex-shrink: 0;
    margin-right: 0.667em;
    width: 1.067em;
    height: 1.067em;
    background-color: ${Colors.white};
    margin-top: 0.2rem;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: ${props => (props.inputType === 'checkbox' ? '0.133em' : '0.667em')};
    vertical-align: -0.167em;
    transition: background-color 0.2s ease-in, border 0.2s ease-in, box-shadow 0.2s ease-in;
  }

  &::after {
    content: '';
    position: absolute;
    top: ${props => (props.inputType === 'checkbox' ? '0.5rem' : '0.2rem')};
    left: ${props => (props.inputType === 'checkbox' ? '0.2em' : '0')};
    width: ${props => (props.inputType === 'checkbox' ? '0.733em' : '1.067em')};
    height: ${props => (props.inputType === 'checkbox' ? '0.667em' : '1.067em')};
    border-radius: ${props => props.inputType === 'radio' && '50%'};
    border: ${props => props.inputType === 'radio' && '0.5rem solid transparent'};
    background-image: ${props => props.inputType === 'checkbox' && `url(${checked})`};
    background-color: ${props => props.inputType === 'radio' && 'transparent'};
    background-position: center;
    background-repeat: no-repeat;
    background-clip: content-box;
    background-size: ${props => props.inputType === 'checkbox' && '0.733em'};
    opacity: 0;
    transform: ${props => (props.inputType === 'checkbox' ? 'translateY(-0.333em)' : 'scale(0)')};
    transition: opacity 0.2s, background-color 0.2s,
      transform 0.2s ease-in ${props => props.inputType === 'radio' && '0.1s'};
  }

  &:hover {
    &::before {
      border-color: ${Colors.rgbaBlack30};
    }
  }
`;

export const IconWrapper = styled('div')`
  margin-right: 0.333em;
`;

export const Icon = styled('img')`
  display: block;
  width: 1.333em;
  height: 1.333em;
  transition: opacity 0.2s ease-in;
`;

export const Input = styled('input')<CheckboxProps>`
  position: absolute;
  clip: rect(0 0 0 0);
  border: 0;
  margin: -0.667em;
  padding: 0;
  width: 0.667em;
  height: 0.667em;
  overflow: hidden;

  &:checked + ${Content} {
    &::before {
      background-color: ${Colors.blueLight50};
    }

    &::after {
      background-color: ${props => props.inputType === 'radio' && Colors.white};
      opacity: 1;
      transform: ${props => (props.inputType === 'checkbox' ? 'translateY(0)' : 'scale(1)')};
    }

    &:hover {
      &::before {
        background-color: ${Colors.blue};
      }
    }
  }

  &:disabled + ${Content} {
    color: ${Colors.rgbaBlack30};

    &::before {
      background-color: ${Colors.rgbaBlack5};
    }

    &::after {
      background-image: none;
      background-color: transparent;
    }

    &:hover {
      &::before {
        border-color: ${Colors.rgbaBlack15};
        background-color: ${Colors.rgbaBlack5};
      }
    }

    ${Icon} {
      opacity: 0.3;
    }
  }

  &:disabled:checked + ${Content} {
    &::after {
      background-image: ${({ inputType }) => inputType === 'checkbox' && `url(${disabledChecked})`};
      background-color: ${({ inputType }) => inputType === 'radio' && Colors.rgbaBlack30};
    }
  }

  &:focus + ${Content} {
    &::before {
      ${checkFocus};
    }
  }

  &:focus:checked + ${Content} {
    &::before {
      border-color: ${Colors.rgbaBlack15};
    }
  }

  &:focus:checked + ${Content}:hover {
    &::before {
      box-shadow: 0 0 0 2px rgba(0, 75, 185, 0.5);
    }
  }
`;

interface LabelProps {
  as?: ElementType;
}

export const Label = styled('label')<LabelProps>`
  display: block;
  cursor: pointer;
`;
